/**
 * 檢核項目明細子表單 | Inspection Record Item Details Form
 * 用於編輯檢驗記錄中的各個檢核項目明細
 */

import { useState, useCallback } from 'react';
import type { InspectionRecordItem, InspectionItem } from '@/types/quality';

interface InspectionItemDetailsProps {
  items: InspectionRecordItem[];
  inspectionItems: InspectionItem[];
  onChange: (items: InspectionRecordItem[]) => void;
  readonly?: boolean;
}

export function InspectionItemDetails({
  items,
  inspectionItems,
  onChange,
  readonly = false,
}: InspectionItemDetailsProps) {
  // 處理實測值變更
  const handleMeasuredValueChange = useCallback(
    (index: number, value: string) => {
      const newItems = [...items];
      newItems[index] = {
        ...newItems[index],
        measuredValue: value,
      };
      onChange(newItems);
    },
    [items, onChange]
  );

  // 處理是否合格變更
  const handleIsQualifiedChange = useCallback(
    (index: number, value: boolean) => {
      const newItems = [...items];
      newItems[index] = {
        ...newItems[index],
        isQualified: value,
      };
      onChange(newItems);
    },
    [items, onChange]
  );

  // 處理偏差值變更
  const handleDeviationChange = useCallback(
    (index: number, value: string) => {
      const newItems = [...items];
      newItems[index] = {
        ...newItems[index],
        deviation: value || undefined,
      };
      onChange(newItems);
    },
    [items, onChange]
  );

  // 處理備註變更
  const handleNotesChange = useCallback(
    (index: number, value: string) => {
      const newItems = [...items];
      newItems[index] = {
        ...newItems[index],
        notes: value || undefined,
      };
      onChange(newItems);
    },
    [items, onChange]
  );

  // 刪除項目
  const handleDeleteItem = useCallback(
    (index: number) => {
      const newItems = items.filter((_, i) => i !== index);
      onChange(newItems);
    },
    [items, onChange]
  );

  // 新增項目
  const handleAddItem = useCallback(() => {
    const newItem: InspectionRecordItem = {
      inspectionItemId: '',
      itemName: '新檢核項目',
      standard: '待定義',
      measuredValue: '',
      isQualified: true,
      notes: '',
    };
    onChange([...items, newItem]);
  }, [items, onChange]);

  if (items.length === 0) {
    return (
      <div className="text-center py-8 bg-slate-50 border-2 border-dashed border-slate-300 rounded-xl">
        <p className="text-sm text-slate-500 mb-2">沒有檢核項目明細</p>
        {!readonly && (
          <button
            type="button"
            onClick={handleAddItem}
            className="px-4 py-2 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 rounded-lg text-sm font-medium transition-colors"
          >
            ➕ 新增檢核項目
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-semibold text-slate-700">檢核項目明細</h4>
        {!readonly && (
          <button
            type="button"
            onClick={handleAddItem}
            className="px-3 py-1.5 bg-emerald-100 hover:bg-emerald-200 text-emerald-700 rounded-lg text-xs font-medium transition-colors"
          >
            ➕ 新增項目
          </button>
        )}
      </div>

      <div className="space-y-3">
        {items.map((item, index) => {
          // 找到對應的檢核項目定義
          const inspectionItem = inspectionItems.find((ii) => ii.id === item.inspectionItemId);

          return (
            <div
              key={index}
              className="p-4 bg-white border-2 border-slate-200 rounded-xl space-y-3 hover:border-emerald-300 transition-colors"
            >
              {/* 頂部：項目資訊 + 刪除按鈕 */}
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm font-semibold text-slate-800">{item.itemName}</span>
                    {item.isQualified ? (
                      <span className="px-2 py-0.5 bg-green-100 text-green-700 rounded-full text-xs font-medium border border-green-300">
                        合格
                      </span>
                    ) : (
                      <span className="px-2 py-0.5 bg-red-100 text-red-700 rounded-full text-xs font-medium border border-red-300">
                        不合格
                      </span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500">標準：{item.standard}</p>
                  {inspectionItem && (
                    <p className="text-xs text-slate-400">
                      方法：{inspectionItem.method} | 重要度：{inspectionItem.criticalLevel}
                    </p>
                  )}
                </div>
                {!readonly && (
                  <button
                    type="button"
                    onClick={() => handleDeleteItem(index)}
                    className="p-1.5 text-red-500 hover:bg-red-50 rounded transition-colors"
                    aria-label="刪除項目"
                  >
                    <span aria-hidden="true">🗑️</span>
                  </button>
                )}
              </div>

              {/* 實測值 */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  實測值 <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={item.measuredValue}
                  onChange={(e) => handleMeasuredValueChange(index, e.target.value)}
                  disabled={readonly}
                  className="w-full px-3 py-2 bg-white border-2 border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-400 focus:border-emerald-300 focus:outline-none disabled:bg-slate-50 disabled:cursor-not-allowed"
                  placeholder="輸入實測值"
                  required
                />
              </div>

              {/* 合格狀態 */}
              {!readonly && (
                <div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={item.isQualified}
                      onChange={(e) => handleIsQualifiedChange(index, e.target.checked)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm font-medium text-slate-700">判定為合格</span>
                  </label>
                </div>
              )}

              {/* 偏差值 */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  偏差值
                </label>
                <input
                  type="text"
                  value={item.deviation || ''}
                  onChange={(e) => handleDeviationChange(index, e.target.value)}
                  disabled={readonly}
                  className="w-full px-3 py-2 bg-white border-2 border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-400 focus:border-emerald-300 focus:outline-none disabled:bg-slate-50 disabled:cursor-not-allowed"
                  placeholder="選填，例：+0.02"
                />
              </div>

              {/* 備註 */}
              <div>
                <label className="block text-xs font-medium text-slate-600 mb-1">
                  備註
                </label>
                <textarea
                  value={item.notes || ''}
                  onChange={(e) => handleNotesChange(index, e.target.value)}
                  disabled={readonly}
                  rows={2}
                  className="w-full px-3 py-2 bg-white border-2 border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-emerald-400 focus:border-emerald-300 focus:outline-none resize-none disabled:bg-slate-50 disabled:cursor-not-allowed"
                  placeholder="選填，輸入備註事項"
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
